<template>
    <div>
        <div class="ribbon">
            <v-btn v-if="complete" class="rb-btn rb-btn--primary" depressed color="primary"
                   :disabled="loading"
                   :loading="loadingComplete"
                   @click="completeTask()">
                <v-icon small class="me-1">mdi-check-circle-outline</v-icon>{{ $t('Complete') }}
            </v-btn>

            <v-btn v-if="rework" class="rb-btn rb-btn--warn" outlined
                   :disabled="loading"
                   :loading="loadingRework"
                   @click="reworkTask()">
                <v-icon small class="me-1">mdi-arrow-u-left-top</v-icon>{{ $t('Rework') }}
            </v-btn>

            <v-btn v-if="redirect" class="rb-btn" outlined color="primary"
                   :disabled="loading"
                   :loading="loadingRedirect"
                   @click="redirectTask()">
                <v-icon small class="me-1">mdi-account-arrow-right-outline</v-icon>{{ $t('Redirect') }}
            </v-btn>

            <v-btn v-if="close" class="rb-btn rb-btn--quiet" text
                   :disabled="loading"
                   :loading="loadingClose"
                   @click="closeTask()">
                <v-icon small class="me-1">mdi-close-circle-outline</v-icon>{{ $t('Close') }}
            </v-btn>

            <v-spacer />

            <v-btn v-if="notes" class="rb-btn" outlined color="primary"
                   :disabled="loading"
                   @click="openNoteDialog()">
                <v-icon small class="me-1">mdi-comment-plus-outline</v-icon>{{ $t('AddNotes') }}
            </v-btn>

            <slot name="action" />
        </div>

        <!-- completion comment: mandatory on the final step and on rework, optional on the other steps -->
        <v-dialog v-model="commentDialog" max-width="560" persistent>
            <v-card dir="rtl" class="rounded-lg">
                <div class="cmt-head">
                    <v-icon small color="white" class="me-2">mdi-comment-text-outline</v-icon>
                    <span>{{ $t('CompletionComment') }}</span>
                </div>
                <div class="pa-5">
                    <label class="cmt-lbl">{{ $t('Comments') }} <span v-if="commentMandatory" style="color:#e53935">*</span></label>
                    <v-textarea v-model="commentText" outlined dense rows="5" auto-grow
                                :rules="commentMandatory ? [v => (!!v && v.trim().length > 0) || $t('CommentRequired')] : []"
                                counter="2000" maxlength="2000"
                                :placeholder="$t('AddComment')" />
                </div>
                <v-card-actions class="px-5 pb-4">
                    <v-spacer />
                    <v-btn text @click="cancelComment">{{ $t('Cancel') }}</v-btn>
                    <v-btn color="primary" depressed :disabled="commentMandatory && (!commentText || !commentText.trim())" @click="confirmComment">
                        <v-icon small class="me-1">mdi-check</v-icon>{{ $t('Complete') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ملاحظات — writes through NotesService to the same store the Notes panel reads,
             so a note added here is listed wherever notes already appear for this task -->
        <v-dialog v-model="noteDialog" max-width="560" persistent>
            <v-card dir="rtl" class="rounded-lg">
                <div class="cmt-head">
                    <v-icon small color="white" class="me-2">mdi-comment-plus-outline</v-icon>
                    <span>{{ $t('AddNotes') }}</span>
                </div>
                <div class="pa-5">
                    <label class="cmt-lbl">{{ $t('Text') }} <span style="color:#e53935">*</span></label>
                    <v-textarea v-model="noteText" outlined dense rows="5" auto-grow
                                counter="3900" maxlength="3900"
                                :placeholder="$t('AddNote')" />
                </div>
                <v-card-actions class="px-5 pb-4">
                    <v-spacer />
                    <v-btn text :disabled="savingNote" @click="cancelNote">{{ $t('Cancel') }}</v-btn>
                    <v-btn color="primary" depressed :loading="savingNote"
                           :disabled="!noteText || !noteText.trim()" @click="saveNote">
                        <v-icon small class="me-1">mdi-check</v-icon>{{ $t('Save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-overlay v-if="loading" />
    </div>
</template>

<script>
    import TasksService from '@/services/TasksService.js'
    import NotesService from '@/services/NotesService.js'

    export default {
        props: {
            // shown on every task page by default; pass :notes="false" to hide it
            notes: {
                type: Boolean,
                default: true
            },
            complete: {
                type: Boolean,
                default: false
            },
            rework: {
                type: Boolean,
                default: false
            },
            redirect: {
                type: Boolean,
                default: false
            },
            close: {
                type: Boolean,
                default: false
            },
            taskId: {
                type: Number,
                required: true,
                default: null
            },
            requireComment: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                loadingComplete: false,
                loadingRework: false,
                loadingRedirect: false,
                loadingClose: false,
                loading: false,

                commentDialog: false,
                commentText: '',
                commentAction: null, // complete , rework
                shouldReworkTask: false,
                shouldCloseTask: false,

                // kept separate from `loading` so saving a note never touches
                // the complete / rework / redirect / close flow
                noteDialog: false,
                noteText: '',
                savingNote: false
            }
        },

        computed: {
            // the comment is required to finish a workflow and to send a task back for rework;
            // on the intermediate steps it is offered but may be left empty
            commentMandatory() {
                return this.commentAction === 'rework' || this.requireComment
            }
        },

        methods: {

            completeTask() {
                // every step asks for a comment, so processes still in progress also carry comments
                this.commentText = ''
                this.commentAction = 'complete'
                this.commentDialog = true
            },

            confirmComment() {
                if (this.commentMandatory && (!this.commentText || !this.commentText.trim())) {
                    return
                }
                this.commentDialog = false
                this.resetLoadings()
                this.loading = true

                if (this.commentAction === 'rework') {  //
                    this.loadingRework = true           //
                    this.shouldReworkTask = true        //
                    this.shouldCloseTask = false        //
                    this.$emit('rework', this.submitTask) //
                } else {
                    this.loadingComplete = true           //
                    this.shouldReworkTask = false           //
                    this.shouldCloseTask = false            //
                    this.$emit('complete', this.submitTask) //
                }
            },

            cancelComment() {
                this.commentDialog = false
                this.commentText = ''
                this.commentAction = null                   //
            },

            async reworkTask() {
                if (await this.$root.$confirm(this.$t('Confirm'), this.$t('ConfirmReworkTask'))) { //
                    this.$nextTick(() => {                                                         //
                        setTimeout(() => {                                                         //
                            this.commentText = ''                                                  //
                            this.commentAction = 'rework'                                          //
                            this.commentDialog = true                                              //
                        }, 150)
                    })
                }
            },

            async redirectTask() {
                if (await this.$root.$confirm(this.$t('Confirm'), this.$t('ConfirmRedirectTask'))) {
                    this.resetLoadings()

                    this.loading = true
                    this.loadingRedirect = true

                    this.shouldReworkTask = true
                    this.shouldCloseTask = false

                    this.$emit('redirect', this.submitTask)
                }
            },

            async closeTask() {
                if (await this.$root.$confirm(this.$t('Confirm'), this.$t('ConfirmCloseTask'))) {
                    this.resetLoadings()
                    this.loading = true
                    this.loadingClose = true

                    this.shouldReworkTask = false
                    this.shouldCloseTask = true

                    this.$emit('close', this.submitTask)
                }
            },

            openNoteDialog() {
                this.noteText = ''
                this.noteDialog = true
            },

            cancelNote() {
                this.noteDialog = false
                this.noteText = ''
            },

            saveNote() {
                const text = (this.noteText || '').trim()
                if (!text) {
                    return
                }
                this.savingNote = true
                NotesService.addNote({ text, activityInstanceId: this.taskId }).then(() => {
                    this.noteDialog = false
                    this.noteText = ''
                    this.$root.$notification(this.$t('NoteAdded'), 'success')
                    // lets a page showing the Notes panel refresh it
                    this.$emit('note-added')
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.savingNote = false
                })
            },

            resetLoadings() {
                this.loading = false
                this.loadingComplete = false
                this.loadingRework = false
                this.loadingRedirect = false
                this.loadingClose = false
            },

            submitTask(inputdata) {
                const postData = {
                    rework: this.shouldReworkTask,
                    close: this.shouldCloseTask,
                    inputdata: (this.commentText && this.commentText.trim())   //
                        ? JSON.stringify({ comment: this.commentText.trim() }) //
                        : inputdata                                            //
                }
                // This was wrapped in `new Promise((error) => { ... })`. The executor's first argument
                // is `resolve`, not `reject`, so calling it in the catch *resolved* the promise on
                // failure, and the success path never settled it at all. Callers ignore the result
                // today, so nothing hung - but any `await submitTask(...)` would have waited forever.
                // Returning the real chain settles correctly; the catch still swallows, so callers
                // see exactly the same resolved-either-way behaviour they see now.
                return TasksService.completeTask(this.taskId, postData).then((res) => {
                    if (res.success) {
                        this.$emit('succeed', true)
                    } else {
                        this.$root.$notification(this.$t(res.message), 'error')
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.resetLoadings()
                })
            }
        }
    }
</script>

<style scoped>
    .ribbon {
        position: sticky;
        bottom: 12px;
        z-index: 2;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 16px;
        padding: 10px 12px;
        border-radius: 16px;
        background: rgba(255, 255, 255, .94);
        border: 1px solid #e6ecea;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .05), 0 12px 28px -14px rgba(15, 23, 42, .35);
    }
    .rb-btn {
        border-radius: 12px !important;
        font-weight: 700;
        letter-spacing: 0;
        text-transform: none;
        height: 40px !important;
        padding: 0 16px !important;
    }
    .rb-btn--primary { box-shadow: 0 8px 18px -10px #0a716e !important; }
    .rb-btn--warn { color: #b45309 !important; border-color: #f0c9a0 !important; }
    .rb-btn--warn:hover { background: #fdf3e7 !important; }
    .rb-btn--quiet { color: #6b7a77 !important; }
    .cmt-head {
        display: flex; align-items: center; font-weight: 700; font-size: 14.5px; color: #fff;
        padding: 13px 20px; background: linear-gradient(135deg, #1B8354 0%, #28a06a 100%);
    }
    .cmt-lbl { display: block; font-weight: 600; font-size: 13px; color: #37474f; margin-bottom: 6px; }
</style>